/**
 * 起動後同期の一部として、HyperLend の converter 追加前に解析済みの EVM 取引を一度だけ再解析対象に戻す。
 * converter はアプリに同梱されるため、datasource の JSON Analyzer のような selector 単位の再解析の仕組みが無い。
 */

import type { RunToDatabase } from '@/types'
import {
  getSyncTimestamp,
  updateEvmTxAnalyzedStatus,
  upsertSyncTimestamp,
} from '@pkg/database-portfolio'

/** 再解析を一度だけ実行したことを記録する sync timestamp の key */
export const reanalyzeHyperLendEvmTxsKey = 'reanalyze-hyperlend-evm-tx'

/**
 * HyperLend の converter が対象とする function selector 一覧
 * - Core Pool: supply / withdraw / borrow / repay
 * - WrappedTokenGateway: depositETH / withdrawETH / borrowETH / repayETH
 * - Isolated Pair (ERC4626): deposit / withdraw
 */
export const hyperLendFunctionIds = [
  '0x617ba037',
  '0x69328dec',
  '0xa415bcad',
  '0x573ade81',
  '0x474cf53d',
  '0x80500d20',
  '0xe74f7b85',
  '0xbcc3c255',
  '0x6e553f65',
  '0xb460af94',
] as const

/** 再解析の完了日時を読み込む関数 */
type LoadCompletedAt = (params: {
  readonly readFromDatabase: RunToDatabase
  readonly key: string
}) => Promise<number>

/** 指定した function selector の取引を再解析対象 (analyzed = false) に戻す関数 */
type MarkEvmTxsUnanalyzed = (params: {
  readonly writeToDatabase: RunToDatabase
  readonly functionIds: readonly string[]
}) => Promise<void>

/** 再解析の完了日時を保存する関数 */
type SaveCompletedAt = (params: {
  readonly writeToDatabase: RunToDatabase
  readonly key: string
  readonly timestamp: number
}) => Promise<void>

/**
 * sync timestamp テーブルから再解析の完了日時を読み込む。
 * @param params DB 読み込み関数と key を受け取る。
 * @returns 完了日時 (UNIX ミリ秒)。未実行の場合は 0 を返す。
 */
const loadCompletedAtFromDatabase: LoadCompletedAt = ({
  readFromDatabase,
  key,
}) => readFromDatabase((database) => getSyncTimestamp({ database, key }))

/**
 * 指定した function selector を持つ EVM 取引を再解析対象に戻す。
 * @param params DB 書き込み関数と function selector 一覧を受け取る。
 * @returns 値は返さない。
 */
const markEvmTxsUnanalyzedInDatabase: MarkEvmTxsUnanalyzed = ({
  writeToDatabase,
  functionIds,
}) =>
  writeToDatabase((database) =>
    updateEvmTxAnalyzedStatus({ database, addresses: [], functionIds }),
  )

/**
 * sync timestamp テーブルに再解析の完了日時を保存する。
 * @param params DB 書き込み関数、key、完了日時を受け取る。
 * @returns 値は返さない。
 */
const saveCompletedAtToDatabase: SaveCompletedAt = ({
  writeToDatabase,
  key,
  timestamp,
}) =>
  writeToDatabase((database) =>
    upsertSyncTimestamp({ database, key, timestamp }),
  )

/**
 * HyperLend の function selector を持つ解析済み EVM 取引を、一度だけ再解析対象に戻す。
 * - 再解析では converter が優先されるため、HyperLend の取引が供給・引出・借入・返済として判定し直される
 * - lock 済みの取引は同期処理側で再解析から除外される
 * - 再解析対象に戻してから完了日時を保存するので、途中で失敗した場合は次回の同期で再試行される
 * - Isolated Pair の selector は ERC4626 共通のため、他チェーンの ERC4626 取引も一度だけ再解析される
 * @param params DB 読み書き関数と、必要に応じて差し替える入出力関数・現在時刻取得関数を受け取る。
 * @returns 値は返さない。
 */
export const reanalyzeHyperLendEvmTxsOnce = async ({
  readFromDatabase,
  writeToDatabase,
  loadCompletedAt = loadCompletedAtFromDatabase,
  markEvmTxsUnanalyzed = markEvmTxsUnanalyzedInDatabase,
  saveCompletedAt = saveCompletedAtToDatabase,
  now = Date.now,
}: {
  readonly readFromDatabase: RunToDatabase
  readonly writeToDatabase: RunToDatabase
  readonly loadCompletedAt?: LoadCompletedAt
  readonly markEvmTxsUnanalyzed?: MarkEvmTxsUnanalyzed
  readonly saveCompletedAt?: SaveCompletedAt
  readonly now?: () => number
}): Promise<void> => {
  const completedAt = await loadCompletedAt({
    readFromDatabase,
    key: reanalyzeHyperLendEvmTxsKey,
  })
  if (completedAt > 0) {
    return
  }

  await markEvmTxsUnanalyzed({
    writeToDatabase,
    functionIds: hyperLendFunctionIds,
  })
  await saveCompletedAt({
    writeToDatabase,
    key: reanalyzeHyperLendEvmTxsKey,
    timestamp: now(),
  })
}
